import * as React from 'react';
import { useRouter } from 'next/router';
import firebase from 'firebase/app';
import { watchViewerTokenChanges } from 'services/client/token';
import { getIsNativePlatform } from 'utils/client/mobile';
import { AuthStatus } from 'constants/auth';
import { Viewer } from 'constants/user';
import { hasDashboardAccess } from 'services/client/token';
import api from 'services/client/api';
import { applyDevViewerOverride } from 'utils/client/devViewerOverride';
import { getVerificationHref, getVerificationReturnPath } from 'utils/client/verificationRecovery';
import {
  authPages,
  privatePages,
  getLoginHrefWithNext,
  EMAIL_VERIFICATION_PAGE,
  SETTINGS_PAGE,
  ALERTS_SETTINGS_PAGE,
} from 'constants/pages';

// @ts-ignore add this to global window object
const INJECTED_PAGE_DATA = typeof window !== 'undefined' ? window?.injectedPageData : undefined;
let hasSetCookie = false;

const DEFAULT_USER: Viewer = {
  status: AuthStatus.Loading,
  viewer: null,
  config: null,
  claims: null,
};

export const ViewerContext = React.createContext(DEFAULT_USER);

const mayInitRetentionSnippet = (user: firebase.User | null) => {
  const email = user?.email;
  const hasEmail = !!email;

  // @ts-ignore need to declare profitwell
  if (hasEmail && typeof profitwell !== 'undefined' && !getIsNativePlatform()) {
    // @ts-ignore need to declare profitwell
    profitwell('start', { user_email: email });
  }
};

export const ViewerProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [viewer, setViewer] = React.useState(DEFAULT_USER);
  const router = useRouter();

  React.useEffect(() => {
    watchViewerTokenChanges(({ user, claims }) => {
      const pathname = router.pathname;

      mayInitRetentionSnippet(user);

      if (user) {
        try {
          if (
            hasDashboardAccess({
              status: AuthStatus.User,
              viewer: user,
              config: null,
              claims,
            }) &&
            !hasSetCookie
            // && !capacitor is mobile
          ) {
            hasSetCookie = true;
            api.get('self/dashboard-state');
          }
        } catch (error) {}

        // The verification screen reads Firebase directly and sends a verified customer to `next`.
        if (
          pathname === EMAIL_VERIFICATION_PAGE ||
          pathname === SETTINGS_PAGE ||
          pathname === ALERTS_SETTINGS_PAGE
        ) {
          setViewer({
            status: AuthStatus.User,
            viewer: user,
            config: null,
            claims,
          });
          return;
        }

        if (privatePages.includes(pathname) && !user.emailVerified) {
          if (getIsNativePlatform()) {
            router.push(getVerificationHref(router.asPath));
          } else {
            window.location.href = getVerificationHref(
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
            );
          }
          return;
        }

        if (authPages.includes(pathname)) {
          if (getIsNativePlatform()) {
            router.push(getVerificationReturnPath(router.query?.next));
          } else {
            window.location.href = getVerificationReturnPath(router.query?.next);
          }
          return;
        }

        setViewer({
          status: AuthStatus.User,
          viewer: user,
          config: null,
          claims,
        });
      } else {
        setViewer({
          status: AuthStatus.Anonymous,
          viewer: null,
          config: null,
          claims: null,
        });

        if (!INJECTED_PAGE_DATA && privatePages.includes(pathname)) {
          // Read live, and with the query string: `pathname` is the route pattern, which is what
          // the check above needs and the wrong thing to come back to. A support link that opens
          // the cancellation flow (`/my/settings?cancel=dashboard`) is carried by the query alone,
          // so dropping it lands an expired session on plain Settings with nothing open.
          const returnTo = `${window.location.pathname}${window.location.search}`;
          router.push(getLoginHrefWithNext(returnTo));
          return;
        }
      }
    });
  }, []);

  // Memoized so a synthetic development viewer keeps its identity across renders; every route
  // change re-renders this provider, and consumers' effects are keyed on the viewer object.
  const value = React.useMemo(() => applyDevViewerOverride(viewer), [viewer]);

  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
};
